// Sign-up flow v2 — all the real API wiring, on the existing axios client.
// Nothing here invents data: every count, name and dollar figure shown in the
// flow is read from an API response, and where an endpoint reports nothing
// per-row the UI says "syncing in the background" instead of making
// something up.

import api, {
  authApi,
  onboardingApi,
  restaurantApi,
  inventoryApi,
  menuApi,
  hireInvitesApi,
  wholesaleApi,
  productionApi,
  invoicesApi,
} from '../../services/api';
import { money, registerType, SHARES, TOOLS, type ToolKey, type TypeKey } from './data';
import type { ImportRowDone, MenuLine, Person, Place, SignupState, VendorRow } from './state';

export const errMsg = (e: any, fallback: string): string =>
  e?.response?.data?.error?.message || e?.message || fallback;

export const errCode = (e: any): string | null => e?.response?.data?.error?.code || null;

export const authed = (): boolean => !!localStorage.getItem('manager_token');

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export async function registerAccount(s: SignupState, honeypot: string): Promise<void> {
  const parts = s.name.trim().split(/\s+/);
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  const q = new URLSearchParams(window.location.search);
  const res = await authApi.register({
    firstName,
    lastName,
    email: s.email.trim(),
    password: s.pw,
    restaurantName: s.biz.trim(),
    restaurantType: registerType(s.type as TypeKey),
    // The real website goes to /onboarding/import-from-website after auth;
    // website_url in the register payload is the server's bot honeypot.
    ...(honeypot ? { website_url: honeypot } : {}),
    ...(q.get('menuToken') ? { menuImportToken: q.get('menuToken')! } : {}),
    acquisition: {
      ...(document.referrer ? { referrer: document.referrer.slice(0, 500) } : {}),
      landing: window.location.href.slice(0, 500),
      ...(q.get('utm_source') ? { utmSource: q.get('utm_source')!.slice(0, 120) } : {}),
      ...(q.get('utm_medium') ? { utmMedium: q.get('utm_medium')!.slice(0, 120) } : {}),
      ...(q.get('utm_campaign') ? { utmCampaign: q.get('utm_campaign')!.slice(0, 120) } : {}),
    },
  });

  const { accessToken, refreshToken, user } = res.data?.data || {};
  if (!accessToken) throw new Error('Something went sideways creating the account. Try again, or sign in if you already have one.');
  localStorage.setItem('manager_token', accessToken);
  localStorage.setItem('manager_refresh_token', refreshToken);
  localStorage.setItem('manager_user', JSON.stringify(user));

  // Catering registers as full_service (not in the enum) — keep the real
  // segment on the account so the dashboard knows what it's shaping.
  if (s.type === 'cater') {
    try { await restaurantApi.updateSettings({ businessType: 'catering' }); } catch { /* retried at finish */ }
  }

  // Shopify App Store install: a parked OAuth token may be waiting — claim it
  // so their store is connected from minute one (same as the old flow).
  const claimShop = sessionStorage.getItem('pending_shopify_claim');
  if (claimShop) {
    sessionStorage.removeItem('pending_shopify_claim');
    try { await api.post('/v1/production/shopify/claim-install', { shop: claimShop }); } catch { /* they can connect in the flow */ }
  }
}

/** Save business edits after the account exists (back → change name). */
export async function saveBusinessEdits(s: SignupState): Promise<void> {
  await onboardingApi.updateRestaurant({ name: s.biz.trim() });
  if (s.type === 'cater') {
    try { await restaurantApi.updateSettings({ businessType: 'catering' }); } catch { /* non-blocking */ }
  }
}

export interface SiteImportResult { itemsCreated: number; details: Record<string, string | null> }

/** Read the owner's website for address/phone/menu — fills only blanks on the
 * server; we mirror the returned details into the Review form. */
export async function importFromWebsite(site: string): Promise<SiteImportResult> {
  const url = /^https?:\/\//.test(site) ? site : `https://${site}`;
  const r = await onboardingApi.importFromWebsite(url);
  const d = r.data?.data || {};
  return { itemsCreated: d.itemsCreated || 0, details: d.details || {} };
}

// ---------------------------------------------------------------------------
// Connect — status checks, OAuth starts, Toast/Faire credentials
// ---------------------------------------------------------------------------

export async function toolConnected(t: ToolKey): Promise<boolean> {
  try {
    switch (t) {
      case 'square': return !!(await wholesaleApi.getSquareStatus()).data?.data?.connected;
      case 'toast': return !!(await api.get('/v1/integrations/toast/status')).data?.data?.connected;
      case 'clover': return !!(await api.get('/v1/integrations/clover/status')).data?.data?.connected;
      case 'lightspeed': return !!(await api.get('/v1/integrations/lightspeed/status')).data?.data?.connected;
      case 'shopify': return !!(await productionApi.getShopifyStatus()).data?.data?.connected;
      case 'faire': return !!(await wholesaleApi.getFaireStatus()).data?.data?.authorized;
      case 'qbo': return !!(await wholesaleApi.getQuickbooksStatus()).data?.data?.connected;
    }
  } catch { return false; }
  return false;
}

/** Kick off a real OAuth round-trip. Returns the provider URL to navigate to.
 * Throws a calm message when the provider isn't switched on server-side. */
export async function startOAuth(t: ToolKey, shopDomain?: string): Promise<string> {
  let res: any;
  try {
    switch (t) {
      case 'square': res = await wholesaleApi.startSquareOauth(); break;
      case 'clover': res = await api.get('/v1/integrations/clover/oauth/start'); break;
      case 'lightspeed': res = await api.get('/v1/integrations/lightspeed/oauth/start'); break;
      case 'qbo': res = await wholesaleApi.startQuickbooksOauth(); break;
      case 'shopify': res = await productionApi.startShopifyOauth((shopDomain || '').trim()); break;
      case 'faire': res = await wholesaleApi.connectFaire('', ''); break;
      default: throw new Error('This one connects differently.');
    }
  } catch (e: any) {
    if (errCode(e) === 'NO_PLATFORM_APP') {
      throw new Error(`${TOOLS[t].n} isn’t switched on for self-serve yet. Book a call and we’ll connect it with you.`);
    }
    if (errCode(e) === 'NO_CREDENTIALS') {
      throw new Error('Faire’s one-click sign-in isn’t switched on yet — paste a brand-portal access token below instead.');
    }
    throw new Error(errMsg(e, `Couldn’t start the ${TOOLS[t].n} connection.`));
  }
  const url = res?.data?.data?.url || res?.data?.data?.authorizeUrl;
  if (!url) throw new Error(`Couldn’t start the ${TOOLS[t].n} connection.`);
  return url;
}

export async function connectToast(creds: { clientId: string; clientSecret: string; locationGuid: string }): Promise<string | null> {
  const r = await api.post('/v1/integrations/toast/connect', creds);
  return r.data?.data?.locationName || null;
}

export async function connectFaireToken(token: string): Promise<void> {
  await wholesaleApi.connectFaire('', '', token.trim());
}

// ---------------------------------------------------------------------------
// Connect — per-tool import plans, driven by real sync responses
// ---------------------------------------------------------------------------

export interface LiveRow { label: string; detail: string | null; state: 'wait' | 'ld' | 'dn' }
export interface ImportOutcome { rows: ImportRowDone[]; patch: (s: SignupState) => Partial<SignupState> }

const SYNC_BG = 'Syncing in the background';

/** Run the real import for a freshly connected tool. `emit` receives row
 * progress for the live checklist; the resolved rows + state patch persist. */
export async function runImport(
  t: ToolKey,
  hint: string | null,
  emit: (rows: LiveRow[]) => void,
): Promise<ImportOutcome> {
  const labels = SHARES[t];
  const rows: LiveRow[] = labels.map((label) => ({ label, detail: null, state: 'wait' }));
  const push = () => emit(rows.map((r) => ({ ...r })));
  const set = (i: number, detail: string) => { rows[i] = { label: labels[i], detail, state: 'dn' }; };
  const run = (i: number) => { rows[i] = { ...rows[i], state: 'ld' }; push(); };
  const doneRows = (): ImportRowDone[] => rows.map((r) => ({ label: r.label, detail: r.detail || SYNC_BG }));
  let patch: (s: SignupState) => Partial<SignupState> = () => ({});

  if (t === 'square') {
    run(1);
    let items = 0;
    try { items = (await wholesaleApi.importSquareMenu()).data?.data?.items || 0; set(1, `${items} items`); }
    catch { set(1, SYNC_BG); }
    push(); run(0);
    try {
      const d = (await wholesaleApi.syncSquare()).data?.data || {};
      const days: Array<{ grossCents: number }> = d.days || [];
      const names: string[] = d.locationNames || [];
      const gross = days.reduce((sum, x) => sum + (x.grossCents || 0), 0);
      set(0, names.length ? `${names.length} found` : 'Connected');
      run(2);
      set(2, days.length ? money(gross / 100) : SYNC_BG);
      patch = (s) => ({
        itemsImported: s.itemsImported + items,
        locationNames: names,
        ...(days.length ? { salesDays: days.map((x) => x.grossCents || 0), salesTotalCents: gross, salesSource: 'Square', salesLabel: 'Last 7 days' } : {}),
      });
    } catch {
      set(0, 'Connected'); set(2, SYNC_BG);
      patch = (s) => ({ itemsImported: s.itemsImported + items });
    }
  } else if (t === 'toast') {
    set(0, hint || 'Connected'); run(1);
    try {
      const d = (await api.post('/v1/integrations/toast/sync', { days: 14 })).data?.data || {};
      const orders = d.totalOrders || 0;
      const gross = d.totalGrossCents || 0;
      set(1, orders ? `${orders} orders · ${money(gross / 100)}` : SYNC_BG);
      patch = () => (orders ? { salesTotalCents: gross, salesSource: 'Toast', salesLabel: 'Last 14 days', salesDays: [] } : {});
    } catch { set(1, SYNC_BG); }
  } else if (t === 'clover') {
    run(0);
    try {
      const sum = (await api.post('/v1/integrations/clover/sync')).data?.data?.summary || {};
      set(0, sum.merchantName || 'Connected'); run(1);
      const salesCents = sum.salesCents || 0;
      set(1, salesCents ? money(salesCents / 100) : SYNC_BG); run(2);
      const tops: Array<{ name: string }> = sum.topItems || [];
      set(2, tops.length ? tops.slice(0, 3).map((x) => x.name).join(', ') : SYNC_BG);
      patch = () => (salesCents ? { salesTotalCents: salesCents, salesSource: 'Clover', salesLabel: 'Recent orders', salesDays: [] } : {});
    } catch { set(0, 'Connected'); set(1, SYNC_BG); set(2, SYNC_BG); }
  } else if (t === 'lightspeed') {
    run(0);
    set(0, 'Connected — read-only');
  } else if (t === 'shopify') {
    run(0);
    try {
      await productionApi.syncShopify();
      set(0, SYNC_BG); set(1, SYNC_BG); set(2, SYNC_BG);
    } catch { set(0, SYNC_BG); set(1, SYNC_BG); set(2, SYNC_BG); }
  } else if (t === 'faire') {
    run(0);
    try {
      const d = (await wholesaleApi.syncFaire()).data?.data || {};
      const created = d.created ?? 0;
      set(0, `${created} new${d.skippedExisting ? ` · ${d.skippedExisting} already in` : ''}`); run(1);
      set(1, 'In Wholesale, under Faire');
      patch = () => ({ faireCreated: created });
    } catch { set(0, SYNC_BG); set(1, SYNC_BG); }
  } else if (t === 'qbo') {
    run(0);
    try {
      const d = (await wholesaleApi.getQuickbooksStatus()).data?.data || {};
      set(0, d.companyName || 'Connected');
    } catch { set(0, 'Connected'); }
    set(1, SYNC_BG); set(2, SYNC_BG);
  }

  push();
  return { rows: doneRows(), patch };
}

// ---------------------------------------------------------------------------
// The no-POS path — a real file through the real menu reader
// ---------------------------------------------------------------------------

export async function importMenuFile(file: File): Promise<{ items: number; categories: number; detail: string }> {
  const r = await menuApi.importMenu(file);
  const d = r.data?.data || {};
  const items = d.items || 0;
  const cats = d.categories || 0;
  return {
    items,
    categories: cats,
    detail: `${items} items${cats ? ` in ${cats} categories` : ''}${d.skippedExisting ? ` · ${d.skippedExisting} already in` : ''}`,
  };
}

/** Snap an invoice → the real invoice reader → the vendor's name. */
export async function vendorFromInvoice(file: File): Promise<string | null> {
  const r = await invoicesApi.parse(file);
  const parsed = r.data?.data?.parsed || {};
  return parsed.vendorName || parsed.vendor || null;
}

// ---------------------------------------------------------------------------
// Review — write everything the owner confirmed to the real account
// ---------------------------------------------------------------------------

export interface ReviewOutcome {
  pushedMenuNames: string[];
  createdVendorNames: string[];
  invitedEmails: string[];
  softErrors: string[];
}

export async function submitReview(s: SignupState): Promise<ReviewOutcome> {
  const out: ReviewOutcome = { pushedMenuNames: [], createdVendorNames: [], invitedEmails: [], softErrors: [] };
  const p: Place = s.place;

  // The one blocking write: the restaurant record needs the place details.
  await onboardingApi.updateRestaurant({
    phone: p.phone.trim(),
    timezone: p.tz,
    addressStreet1: p.street.trim(),
    addressCity: p.city.trim(),
    ...(p.state.trim() ? { addressState: p.state.trim() } : {}),
    ...(p.zip.trim() ? { addressPostalCode: p.zip.trim() } : {}),
  });

  // Free-text hours live in settings (operatingHours is a structured day-map
  // the dashboard's Settings page edits — not worth a wrong guess here).
  if (p.hours.trim()) {
    try { await restaurantApi.updateSettings({ hoursText: p.hours.trim() }); }
    catch { out.softErrors.push('hours'); }
  }

  // Pasted menu lines that now have a price → real menu items.
  const priced = s.menu.filter((m) => !m.pushed).map((m) => {
    const override = parseFloat(s.prices[m.name] || '');
    const dollars = override > 0 ? override : m.price || 0;
    return { line: m, priceCents: Math.round(dollars * 100) };
  }).filter((x) => x.priceCents > 0);
  if (priced.length) {
    const byCat = new Map<string, Array<{ name: string; priceCents: number }>>();
    for (const x of priced) {
      const cat = x.line.category || 'Menu';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push({ name: x.line.name, priceCents: x.priceCents });
    }
    try {
      await onboardingApi.quickMenuSetup({ categories: [...byCat.entries()].map(([name, items]) => ({ name, items })) });
      out.pushedMenuNames = priced.map((x) => x.line.name);
    } catch { out.softErrors.push('menu'); }
  }

  // Suppliers → real vendors, so ordering has someone to order from.
  for (const v of s.vendors) {
    if (v.created || !v.name.trim()) continue;
    try {
      await inventoryApi.createVendor({ name: v.name.trim(), ...(v.email.trim() ? { email: v.email.trim() } : {}) });
      out.createdVendorNames.push(v.name.trim());
    } catch (e: any) {
      // An existing vendor with the same name is fine — anything else is soft.
      if (!/exist/i.test(errMsg(e, ''))) out.softErrors.push(`supplier ${v.name}`);
      else out.createdVendorNames.push(v.name.trim());
    }
  }

  // Team → hire invites. The real invite goes by EMAIL (with a link to set
  // their own PIN); phone rides along for their profile.
  if (s.invite) {
    const r = await sendInvites(s.team);
    out.invitedEmails = r.sent;
    out.softErrors.push(...r.failed.map((n) => `invite ${n}`));
  }

  return out;
}

/** Create (and thereby email) a hire invite for every ready, uninvited person.
 * Creating the invite sends the full onboarding email itself. */
export async function sendInvites(team: Person[]): Promise<{ sent: string[]; failed: string[] }> {
  const sent: string[] = [];
  const failed: string[] = [];
  for (const person of team) {
    if (person.invited || !inviteReady(person)) continue;
    try {
      await hireInvitesApi.create({
        firstName: person.first.trim(),
        lastName: person.last.trim(),
        email: person.email.trim(),
        ...(person.phone.trim() ? { phone: person.phone.trim() } : {}),
        primaryRole: (person.role || 'Server').toLowerCase(),
      });
      sent.push(person.email.trim());
    } catch { failed.push(person.first); }
  }
  return { sent, failed };
}

export const inviteReady = (p: Person): boolean =>
  !!(p.first.trim() && p.last.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p.email.trim()));

// ---------------------------------------------------------------------------
// Portions → modules → done
// ---------------------------------------------------------------------------

/** Map the flow's portion ids onto the dashboard's settings.modules keys.
 * ops→inventory/purchasing/recipes, staff→team, guest→frontOfHouse,
 * batch→production, whole→wholesale, events→events. 'pos' has no manager
 * module switch (POS is its own app); sales+menu stay on — they're where
 * synced data lands. Each segment's core plan carries its own base modules
 * (bakery Essentials = counts/ordering/recipes; Mise Bar = bar inventory).
 * CPG matches the template register already applied. */
export function modulesFor(type: TypeKey, on: string[]): Record<string, boolean> {
  if (type === 'cpg') {
    return { wholesale: true, production: true, inventory: true, recipes: true, purchasing: true, menu: false, sales: false, frontOfHouse: false, team: false, events: false };
  }
  const has = (id: string) => on.includes(id);
  if (type === 'bake') {
    return {
      sales: true, menu: true, recipes: true, inventory: true, purchasing: true,
      production: has('batch'), wholesale: has('whole'), team: has('staff'),
      frontOfHouse: false, events: false,
    };
  }
  if (type === 'bar') {
    return {
      sales: true, menu: true, inventory: true, purchasing: true, recipes: false,
      team: has('staff'), events: has('events'),
      frontOfHouse: false, production: false, wholesale: false,
    };
  }
  // rest / qsr / cater — Base + chosen portions.
  return {
    sales: true,
    menu: true,
    recipes: has('ops'),
    inventory: has('ops') || has('bar'),
    purchasing: has('ops'),
    team: has('staff'),
    frontOfHouse: has('guest'),
    events: has('events'),
    production: false,
    wholesale: false,
  };
}

export async function finishSetup(type: TypeKey, on: string[]): Promise<void> {
  await restaurantApi.updateSettings({
    modules: modulesFor(type, on),
    ...(type === 'cater' ? { businessType: 'catering' } : {}),
  });
  await onboardingApi.complete();
}

// ---------------------------------------------------------------------------
// Resume — rebuild enough state for an authed return visit
// ---------------------------------------------------------------------------

export interface MeSnapshot {
  restaurantName: string | null;
  restaurantType: string | null;
  status: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export async function fetchMe(): Promise<MeSnapshot | null> {
  try {
    const d = (await authApi.me()).data?.data;
    const r = d?.restaurants?.[0];
    if (!r) return null;
    return {
      restaurantName: r.name || null,
      restaurantType: r.type || null,
      status: r.status || null,
      email: d.email || d.user?.email || null,
      firstName: d.firstName || d.user?.firstName || null,
      lastName: d.lastName || d.user?.lastName || null,
    };
  } catch { return null; }
}

export interface CurrentRestaurant {
  phone: string | null; timezone: string | null;
  addressStreet1: string | null; addressCity: string | null; addressState: string | null; addressPostalCode: string | null;
  settings: any;
}

export async function fetchCurrentRestaurant(): Promise<CurrentRestaurant | null> {
  try { return (await restaurantApi.getCurrent()).data?.data || null; } catch { return null; }
}

/** 15-minute setup call → the real setup-help request (a person emails back
 * to confirm a time — nothing here pretends a slot is booked). */
export async function requestSetupCall(stepName: string, preferred: string, topic: string | null, email: string): Promise<void> {
  await onboardingApi.requestHelp({
    currentStep: stepName,
    message: `Setup call request from the sign-up flow. Preferred: ${preferred}.${topic ? ` Topic: ${topic}.` : ''} Reply to ${email}.`,
  });
}
